import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import {
  BrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom'
import { loadSentry } from './utils/sentryLazy'
import { ThemeProvider } from './context/ThemeContext'
import { RoleProvider } from './context/RoleContext'
import { UserRoleProvider } from './context/UserRoleContext'
import ErrorBoundary from './components/shared/ErrorBoundary'
// Fuentes autoalojadas: subset latin y solo los pesos que usa el código. Reemplazan los
// @import a fonts.googleapis.com, que la CSP (style-src / font-src 'self') bloqueaba.
// Inter 400-900 · JetBrains Mono 400-700 (font-black cae en 700, como antes) ·
// Playfair Display 400/600/700 y cursiva 400/600 (lo que cargaba Google). El logo del menú
// (.brand-logo) NO usa Playfair: es Georgia del sistema (ver src/styles/brand.css).
import '@fontsource/inter/latin-400.css'
import '@fontsource/inter/latin-500.css'
import '@fontsource/inter/latin-600.css'
import '@fontsource/inter/latin-700.css'
import '@fontsource/inter/latin-800.css'
import '@fontsource/inter/latin-900.css'
import '@fontsource/jetbrains-mono/latin-400.css'
import '@fontsource/jetbrains-mono/latin-500.css'
import '@fontsource/jetbrains-mono/latin-600.css'
import '@fontsource/jetbrains-mono/latin-700.css'
import '@fontsource/playfair-display/latin-400.css'
import '@fontsource/playfair-display/latin-600.css'
import '@fontsource/playfair-display/latin-700.css'
import '@fontsource/playfair-display/latin-400-italic.css'
import '@fontsource/playfair-display/latin-600-italic.css'
import './index.css'
import App from './App'
// Dev helper — available as window.generateTestXmls() in browser console
import './utils/testDataGenerator'

// P-3 (audit 2026-07-06): Sentry se carga con dynamic import después del
// primer render — misma config que antes, fuera del main chunk. Los eventos
// previos a la carga se encolan en sentryLazy (withSentry).
loadSentry((Sentry) => {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    enabled: import.meta.env.MODE === 'production',
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    tracePropagationTargets: [
      /^https:\/\/.*\.supabase\.co\/rest/,
      /^\/api\//,
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = event.request.url.replace(/\d{11}/g, '[RUC_REDACTADO]')
      }
      return event
    },
  })
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <ThemeProvider>
        <RoleProvider>
          <UserRoleProvider>
            <ErrorBoundary fallbackTitle="Algo salió mal">
              <App />
            </ErrorBoundary>
          </UserRoleProvider>
        </RoleProvider>
      </ThemeProvider>
    </BrowserRouter>
  </StrictMode>,
)
